'use client'

import { useMemo } from 'react'
import dynamic from 'next/dynamic'
import { useRouter } from '@/i18n/navigation'
import { Link } from '@/i18n/navigation'
import { fetchMarketOverview, type MarketRegionData } from '@/services/marketApi'
import { fetchCryptoPrices, type CryptoPrice } from '@/services/crypto'
import type { SidebarData } from '@/services/sidebarApi'
import { useLocale } from 'next-intl'
import { t } from '@/i18n/translations'
import FlagIcon from './FlagIcon'
import { usePolledData } from '@/hooks/usePolledData'

const MarketSidebar = dynamic(() => import('./MarketSidebar'), {
  loading: () => (
    <div className="border rounded-lg animate-pulse" style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)', height: 400 }} />
  ),
})

interface MarketIndex {
  id: string
  countryCode: string
  name: string
  desc: string
  value: number
  changePercent: number
  diff: number
  monthly: number
  yearly: number
  currency?: string
  currencyCode?: string
  micCode?: string
  exchangeName?: string
}

interface MarketRegion {
  title: string
  accent: string
  markets: MarketIndex[]
}

// Region kod → i18n key ve renk
const regionMeta: Record<string, { key: string; accent: string }> = {
  US: { key: 'region.us', accent: '#3B82F6' },
  EU: { key: 'region.eu', accent: '#A81E1E' },
  APAC: { key: 'region.apac', accent: '#E5A00D' },
  AMERICAS: { key: 'region.us', accent: '#3B82F6' },
  MEA: { key: 'region.mea', accent: '#10B981' },
  GLOBAL: { key: 'Global', accent: '#8B5CF6' },
}

// Frontend'de birleştirilecek region'lar (US + AMERICAS → tek "Amerika Borsaları")
const mergeRegions: Record<string, string> = {
  AMERICAS: 'US',
}

// Para birimi: sembol, pozisyon (prefix/suffix), ondalık ayracı
// prefix → sembol solda ($100), suffix → sembol sağda (100 ₺)
// dot → Anglo-sakson (1,234.56), comma → kıta Avrupası (1.234,56)
const currencyInfo: Record<string, { symbol: string; pos: 'prefix' | 'suffix'; dec: 'dot' | 'comma' }> = {
  // Sembol solda, nokta ondalık
  USD:  { symbol: '$',    pos: 'prefix', dec: 'dot' },
  GBP:  { symbol: '£',    pos: 'prefix', dec: 'dot' },
  CAD:  { symbol: 'C$',   pos: 'prefix', dec: 'dot' },
  AUD:  { symbol: 'A$',   pos: 'prefix', dec: 'dot' },
  NZD:  { symbol: 'NZ$',  pos: 'prefix', dec: 'dot' },
  HKD:  { symbol: 'HK$',  pos: 'prefix', dec: 'dot' },
  SGD:  { symbol: 'S$',   pos: 'prefix', dec: 'dot' },
  TWD:  { symbol: 'NT$',  pos: 'prefix', dec: 'dot' },
  MXN:  { symbol: 'MX$',  pos: 'prefix', dec: 'dot' },
  COP:  { symbol: 'COL$', pos: 'prefix', dec: 'dot' },
  JMD:  { symbol: 'J$',   pos: 'prefix', dec: 'dot' },
  ARS:  { symbol: 'AR$',  pos: 'prefix', dec: 'dot' },
  BRL:  { symbol: 'R$',   pos: 'prefix', dec: 'comma' },
  ZAR:  { symbol: 'R',    pos: 'prefix', dec: 'dot' },
  BWP:  { symbol: 'P',    pos: 'prefix', dec: 'dot' },
  JPY:  { symbol: '¥',    pos: 'prefix', dec: 'dot' },
  CNY:  { symbol: '¥',    pos: 'prefix', dec: 'dot' },
  KRW:  { symbol: '₩',    pos: 'prefix', dec: 'dot' },
  INR:  { symbol: '₹',    pos: 'prefix', dec: 'dot' },
  PHP:  { symbol: '₱',    pos: 'prefix', dec: 'dot' },
  PKR:  { symbol: '₨',    pos: 'prefix', dec: 'dot' },
  ILS:  { symbol: '₪',    pos: 'prefix', dec: 'dot' },
  EGP:  { symbol: 'E£',   pos: 'prefix', dec: 'dot' },
  MYR:  { symbol: 'RM',   pos: 'prefix', dec: 'dot' },
  IDR:  { symbol: 'Rp',   pos: 'prefix', dec: 'dot' },
  THB:  { symbol: '฿',    pos: 'prefix', dec: 'dot' },
  RUB:  { symbol: '₽',    pos: 'suffix', dec: 'comma' },
  // Sembol sağda, virgül ondalık
  TRY:  { symbol: '₺',    pos: 'suffix', dec: 'comma' },
  EUR:  { symbol: '€',    pos: 'suffix', dec: 'comma' },
  CHF:  { symbol: 'CHF',  pos: 'suffix', dec: 'comma' },
  SEK:  { symbol: 'kr',   pos: 'suffix', dec: 'comma' },
  DKK:  { symbol: 'kr',   pos: 'suffix', dec: 'comma' },
  NOK:  { symbol: 'kr',   pos: 'suffix', dec: 'comma' },
  PLN:  { symbol: 'zł',   pos: 'suffix', dec: 'comma' },
  CZK:  { symbol: 'Kč',   pos: 'suffix', dec: 'comma' },
  HUF:  { symbol: 'Ft',   pos: 'suffix', dec: 'comma' },
  RON:  { symbol: 'lei',  pos: 'suffix', dec: 'comma' },
  ISK:  { symbol: 'kr',   pos: 'suffix', dec: 'comma' },
  PEN:  { symbol: 'S/',   pos: 'prefix', dec: 'dot' },
  CLP:  { symbol: 'CLP',  pos: 'prefix', dec: 'dot' },
  SAR:  { symbol: 'SR',   pos: 'suffix', dec: 'dot' },
  AED:  { symbol: 'AED',  pos: 'suffix', dec: 'dot' },
  QAR:  { symbol: 'QAR',  pos: 'suffix', dec: 'dot' },
  KWD:  { symbol: 'KWD',  pos: 'suffix', dec: 'dot' },
}

function getCurrencySymbol(currency: string): string | undefined {
  return currencyInfo[currency]?.symbol
}

function mapApiToRegions(apiData: MarketRegionData[]): MarketRegion[] {
  // Önce region'ları düz listeye çevir
  const regionMap = new Map<string, MarketRegion>()

  // Gösterim sırası
  // BIST/Türkiye kaldırıldı — TR bölgesi anasayfada gösterilmez
  const displayOrder = ['US', 'EU', 'APAC', 'MEA', 'GLOBAL']

  for (const r of apiData) {
    const hasIndices = r.exchanges.some(e => e.indices.length > 0)
    if (!hasIndices) continue

    // Merge hedefi varsa oraya ekle (AMERICAS → US)
    const targetKey = mergeRegions[r.region] || r.region
    const meta = regionMeta[targetKey] || { key: r.name, accent: '#6B7280' }

    if (!regionMap.has(targetKey)) {
      regionMap.set(targetKey, { title: meta.key, accent: meta.accent, markets: [] })
    }

    const region = regionMap.get(targetKey)!

    for (const ex of r.exchanges) {
      for (const idx of ex.indices) {
        region.markets.push({
          id: idx.symbol.toLowerCase(),
          countryCode: ex.countryCode.toLowerCase(),
          name: idx.symbol,
          desc: idx.name,
          value: idx.close,
          changePercent: idx.changePercent,
          diff: idx.diff,
          monthly: idx.monthly ?? 0,
          yearly: idx.yearly ?? 0,
          currency: getCurrencySymbol(ex.currency),
          currencyCode: ex.currency,
          micCode: ex.micCode,
          exchangeName: ex.name,
        })
      }
    }
  }

  // Sıraya göre döndür
  return displayOrder
    .filter(key => regionMap.has(key))
    .map(key => regionMap.get(key)!)
}

function fmtNumber(val: number, dec: 'dot' | 'comma'): string {
  const fixed = val.toFixed(2)
  const [int, d] = fixed.split('.')
  if (dec === 'dot') return int.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '.' + d
  return int.replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + d
}

function formatValue(val: number, _currency?: string, currencyCode?: string): string {
  const info = currencyCode ? currencyInfo[currencyCode] : undefined
  const num = fmtNumber(val, info?.dec ?? 'comma')
  if (!info) return num
  return info.pos === 'prefix' ? info.symbol + num : num + ' ' + info.symbol
}

function formatDiff(val: number, _currency?: string, currencyCode?: string): string {
  const info = currencyCode ? currencyInfo[currencyCode] : undefined
  const num = fmtNumber(Math.abs(val), info?.dec ?? 'comma')
  if (!info) return num
  return info.pos === 'prefix' ? info.symbol + num : num + ' ' + info.symbol
}

function formatPercent(val: number): string {
  return val.toFixed(2).replace('.', ',')
}

function Val({ val, suffix, bold }: { val: number; suffix?: string; bold?: boolean }) {
  const color = val > 0 ? 'var(--chart-up)' : val < 0 ? 'var(--chart-down)' : 'var(--text-main)'
  const arrow = val > 0 ? ' ↑' : val < 0 ? ' ↓' : ''
  return (
    <span className={`tabular-nums ${bold ? 'font-semibold' : 'font-medium'}`} style={{ color }}>
      {formatPercent(Math.abs(val))}{suffix || ''}{arrow}
    </span>
  )
}

function Skeleton({ w, h = 14, className = '' }: { w: number; h?: number; className?: string }) {
  return (
    <div
      className={`animate-pulse rounded ${className}`}
      style={{ width: w, height: h, backgroundColor: 'var(--border-color)' }}
    />
  )
}

function RegionTable({ region, loading, lazy }: { region: MarketRegion; loading?: boolean; lazy?: boolean }) {
  const router = useRouter()
  const lang = useLocale()
  return (
    <div className={`mb-5${lazy ? ' content-auto' : ''}`}>
      <h2 className="text-base md:text-xl font-extrabold m-0 mb-2 md:mb-2.5 tracking-tight" style={{ color: 'var(--text-main)' }}>
        {t(region.title, lang)}
      </h2>

      <div className="border overflow-hidden rounded-lg" style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)' }}>
        <table className="w-full text-left border-collapse table-fixed md:table-auto">
          <colgroup className="md:hidden">
            <col style={{ width: '45%' }} />
            <col style={{ width: '35%' }} />
            <col style={{ width: '20%' }} />
          </colgroup>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--border-color)' }}>
              <th scope="col" className="px-3 md:px-4 py-2 text-[11px] font-bold uppercase tracking-widest" style={{ color: 'var(--text-muted)' }}>{t('th.exchange', lang)}</th>
              <th scope="col" className="px-2 py-2 text-[11px] font-bold uppercase tracking-widest text-right" style={{ color: 'var(--text-muted)' }}>{t('th.last', lang)}</th>
              <th scope="col" className="px-2 py-2 text-[11px] font-bold uppercase tracking-widest text-right" style={{ color: 'var(--text-muted)' }}>%</th>
              <th scope="col" className="hidden md:table-cell px-2 py-2 text-[11px] font-bold uppercase tracking-widest text-right" style={{ color: 'var(--text-muted)' }}>{t('th.change', lang)}</th>
              <th scope="col" className="hidden md:table-cell px-2 py-2 text-[11px] font-bold uppercase tracking-widest text-right" style={{ color: 'var(--text-muted)' }}>{t('th.monthly', lang)}</th>
              <th scope="col" className="hidden md:table-cell px-4 py-2 text-[11px] font-bold uppercase tracking-widest text-right" style={{ color: 'var(--text-muted)' }}>{t('th.yearly', lang)}</th>
            </tr>
          </thead>
          <tbody>
            {loading
              ? region.markets.map((m, i) => (
                  <tr key={m.id} style={{ borderBottom: i < region.markets.length - 1 ? '1px solid var(--border-color)' : 'none' }}>
                    <td className="px-3 md:px-4 py-2.5 md:py-3">
                      <div className="flex items-start gap-2">
                        <Skeleton w={18} h={14} />
                        <div>
                          <div className="text-[12px] md:text-[13px] font-bold leading-tight" style={{ color: 'var(--text-main)' }}>{m.name}</div>
                          <div className="text-[9px] mt-0.5 leading-tight uppercase tracking-wide hidden md:block" style={{ color: 'var(--text-muted)' }}>{m.desc}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-2 py-3 text-right"><Skeleton w={80} className="ml-auto" /></td>
                    <td className="px-2 py-3 text-right"><Skeleton w={50} className="ml-auto" /></td>
                    <td className="hidden md:table-cell px-2 py-3 text-right"><Skeleton w={55} className="ml-auto" /></td>
                    <td className="hidden md:table-cell px-2 py-3 text-right"><Skeleton w={45} className="ml-auto" /></td>
                    <td className="hidden md:table-cell px-4 py-3 text-right"><Skeleton w={45} className="ml-auto" /></td>
                  </tr>
                ))
              : region.markets.map((m, i) => {
                  const hasData = m.value > 0
                  return (
                    <tr
                      key={m.id}
                      className="cursor-pointer transition-colors duration-100 hover:bg-[var(--bg-body)]"
                      style={{ borderBottom: i < region.markets.length - 1 ? '1px solid var(--border-color)' : 'none' }}
                      onClick={() => router.push({ pathname: '/market/[id]', params: { id: m.id } })}
                    >
                      <td className="px-3 md:px-4 py-2.5 md:py-3">
                        <div className="flex items-center gap-1.5 md:gap-2">
                          {hasData ? (
                            <span className="text-[10px] hidden md:inline" style={{ color: m.changePercent >= 0 ? 'var(--chart-up)' : 'var(--chart-down)' }}>
                              {m.changePercent >= 0 ? '↑' : '↓'}
                            </span>
                          ) : (
                            <Skeleton w={8} h={8} className="hidden md:block" />
                          )}
                          <div>
                            <div className="text-[12px] md:text-[13px] font-bold leading-tight flex items-center gap-1.5 md:gap-2" style={{ color: 'var(--text-main)' }}>
                              <FlagIcon code={m.countryCode} size={18} />
                              {m.name}
                            </div>
                            <div className="text-[9px] mt-0.5 leading-tight uppercase tracking-wide flex items-center gap-1 overflow-hidden" style={{ color: 'var(--text-muted)' }}>
                              <span className="truncate">{m.desc}</span>
                              {m.micCode && (
                                <>
                                  <span className="shrink-0">·</span>
                                  <span
                                    className="shrink-0 hover:underline cursor-pointer"
                                    style={{ color: 'var(--accent-color)' }}
                                    onClick={(e) => { e.stopPropagation(); router.push({ pathname: '/exchange/[micCode]', params: { micCode: m.micCode! } }) }}
                                  >
                                    {m.micCode}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-2 py-2.5 md:py-3 text-right whitespace-nowrap">
                        {hasData ? (
                          <span className="text-[11px] md:text-[13px] font-semibold tabular-nums" style={{ color: m.changePercent >= 0 ? 'var(--chart-up)' : 'var(--chart-down)' }}>
                            {formatValue(m.value, m.currency, m.currencyCode)} {m.changePercent >= 0 ? '↑' : '↓'}
                          </span>
                        ) : <Skeleton w={80} className="ml-auto" />}
                      </td>
                      <td className="px-2 py-2.5 md:py-3 text-right text-[11px] md:text-[13px] whitespace-nowrap">
                        {hasData ? <Val val={m.changePercent} suffix="%" /> : <Skeleton w={50} className="ml-auto" />}
                      </td>
                      <td className="hidden md:table-cell px-2 py-3 text-right text-[13px]">
                        {hasData ? (
                          <span className="tabular-nums font-medium" style={{ color: m.diff >= 0 ? 'var(--chart-up)' : 'var(--chart-down)' }}>
                            {formatDiff(m.diff, m.currency, m.currencyCode)}
                          </span>
                        ) : <Skeleton w={55} className="ml-auto" />}
                      </td>
                      <td className="hidden md:table-cell px-2 py-3 text-right text-[13px]">
                        {hasData ? <Val val={m.monthly} suffix="%" /> : <Skeleton w={45} className="ml-auto" />}
                      </td>
                      <td className="hidden md:table-cell px-4 py-3 text-right text-[13px]">
                        {hasData ? <Val val={m.yearly} suffix="%" /> : <Skeleton w={45} className="ml-auto" />}
                      </td>
                    </tr>
                  )
                })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ───── HomeCryptoStrip ─────

const TOP_CRYPTOS = ['BTC', 'ETH', 'XRP', 'BNB']

const CRYPTO_NAMES: Record<string, string> = {
  BTC: 'Bitcoin', ETH: 'Ethereum', XRP: 'XRP', BNB: 'BNB',
}

function fmtCryptoPrice(v: number): string {
  if (v >= 1) return '$' + v.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return '$' + v.toPrecision(4)
}

function HomeCryptoStrip({ initialPrices }: { initialPrices?: Record<string, CryptoPrice> }) {
  const lang = useLocale()
  const router = useRouter()
  const { data, loading } = usePolledData(
    () => fetchCryptoPrices(TOP_CRYPTOS),
    initialPrices && Object.keys(initialPrices).length ? initialPrices : undefined,
    60_000,
  )
  const prices = data ?? {}

  return (
    <div className="mb-5">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-base md:text-xl font-extrabold m-0 tracking-tight" style={{ color: 'var(--text-main)' }}>
          {lang === 'en' ? 'Cryptocurrencies' : 'Kripto Paralar'}
        </h2>
        <Link
          href="/crypto"
          className="text-[12px] font-semibold hover:underline"
          style={{ color: 'var(--accent-color)' }}
        >
          {lang === 'en' ? 'See All →' : 'Tümü →'}
        </Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {TOP_CRYPTOS.map(sym => {
          const p = prices[sym]
          const up = p ? p.usd_24h_change >= 0 : true
          const clr = up ? 'var(--chart-up)' : 'var(--chart-down)'

          return (
            <div
              key={sym}
              className="border rounded-lg p-3 cursor-pointer transition-colors duration-100 hover:bg-[var(--bg-body)]"
              style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)' }}
              onClick={() => router.push({ pathname: '/crypto/[symbol]', params: { symbol: sym.toLowerCase() } })}
            >
              <div className="flex items-center gap-2.5 mb-2">
                {loading ? (
                  <Skeleton w={32} h={32} className="rounded-full" />
                ) : (
                  <img
                    src={`/crypto-icons/${sym.toLowerCase()}.png`}
                    alt={sym}
                    width={32}
                    height={32}
                    /* P2 (Temmuz 2026 denetimi): bu ikonlar ilk ekranda görünmesine
                       rağmen yükleme önceliği belirtilmemişti. */
                    loading="eager"
                    fetchPriority="high"
                    decoding="async"
                    className="rounded-full shrink-0"
                    onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
                  />
                )}
                <div className="min-w-0">
                  <div className="text-[13px] font-bold leading-tight truncate" style={{ color: 'var(--text-main)' }}>{sym}</div>
                  <div className="text-[10px] leading-tight truncate" style={{ color: 'var(--text-muted)' }}>{CRYPTO_NAMES[sym]}</div>
                </div>
              </div>
              {loading ? (
                <>
                  <Skeleton w={80} h={14} className="mb-1" />
                  <Skeleton w={50} h={12} />
                </>
              ) : p ? (
                <>
                  <div className="text-[13px] font-bold tabular-nums" style={{ color: 'var(--text-main)' }}>
                    {fmtCryptoPrice(p.usd)}
                  </div>
                  <div className="text-[11px] font-semibold tabular-nums mt-0.5" style={{ color: clr }}>
                    {up ? '+' : ''}{p.usd_24h_change.toFixed(2).replace('.', ',')}%{up ? ' ↑' : ' ↓'}
                  </div>
                </>
              ) : (
                <div className="text-[12px]" style={{ color: 'var(--text-muted)' }}>—</div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

interface GlobalMarketsProps {
  initialData?: MarketRegionData[]
  initialCrypto?: Record<string, CryptoPrice>
  initialSidebar?: SidebarData | null
  /** E2: kenar çubuğu grafiğinin SSR tohumu. */
  initialSidebarChart?: number[] | null
}

export default function GlobalMarkets({ initialData, initialCrypto, initialSidebar, initialSidebarChart }: GlobalMarketsProps) {
  const lang = useLocale()
  const { data: apiData } = usePolledData(
    fetchMarketOverview,
    initialData && initialData.length ? initialData : undefined,
    60_000,
  )
  const regions = useMemo(() => (apiData ? mapApiToRegions(apiData) : []), [apiData])
  const loading = apiData === undefined

  return (
    <section style={{ contain: 'layout style' }}>
      <div className="flex flex-col lg:flex-row gap-5">
        {/* Sol: Borsa Tabloları */}
        <div className="flex-1 min-w-0" style={{ minHeight: loading ? 1200 : 'auto' }}>
          {loading ? (
            // İlk yükleme skeleton — 5 bölge, her biri 6 satır (gerçek içerikle eşleşiyor)
            <>
              {[1, 2, 3, 4, 5].map(g => (
                <div key={g} className="mb-5">
                  <Skeleton w={200} h={24} className="mb-2" />
                  <div className="border overflow-hidden rounded-lg" style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)' }}>
                    {[1,2,3,4,5,6].map(i => (
                      <div key={i} className="flex items-center justify-between px-3 py-2.5" style={{ borderBottom: i < 6 ? '1px solid var(--border-color)' : 'none' }}>
                        <div className="flex items-center gap-2">
                          <Skeleton w={18} h={14} />
                          <Skeleton w={80} h={14} />
                        </div>
                        <div className="flex items-center gap-3">
                          <Skeleton w={70} h={14} />
                          <Skeleton w={50} h={14} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </>
          ) : regions.length === 0 ? (
            <div className="text-center py-10" style={{ color: 'var(--text-muted)' }}>
              {t('status.noData', lang)}
            </div>
          ) : (
            <>
              {regions.map((region, i) => (
                <RegionTable key={region.title} region={region} lazy={i > 0} />
              ))}
              <HomeCryptoStrip initialPrices={initialCrypto} />
            </>
          )}
        </div>

        {/* Sağ: Piyasa Sidebar */}
        <div className="w-full lg:w-[420px] lg:flex-shrink-0">
          <MarketSidebar initialData={initialSidebar} initialChart={initialSidebarChart} />
        </div>
      </div>
    </section>
  )
}
